import {useCallback, useEffect, useMemo, useState} from 'react';
import type {
  CloudWatchLogEventResponse,
  CloudWatchLogGroupResponse,
} from 'src/api/ProactApiClient';
import {CloudWatchLogLevel} from 'src/api/ProactApiClient';
import {useLazyGetCloudWatchLogsQuery} from 'src/redux/queries/cloudWatch';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  DEFAULT_LOG_RANGE_MINUTES,
  LOG_PAGE_LIMIT,
  logWindowFromMinutes,
} from './cloudWatchDisplay';

interface LogWindow {
  start: string;
  end: string;
}

export const useCloudWatchLogSearch = (
  groups: CloudWatchLogGroupResponse[]
) => {
  const [group, setGroup] = useState('');
  const [minutes, setMinutes] = useState(DEFAULT_LOG_RANGE_MINUTES);
  const [level, setLevel] = useState<CloudWatchLogLevel>(
    CloudWatchLogLevel.All
  );
  const [text, setText] = useState('');

  const [events, setEvents] = useState<CloudWatchLogEventResponse[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [searchError, setSearchError] = useState<string | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);
  // Pinned when Search runs so Load more pages the same window, not a moving "now".
  const [searchWindow, setSearchWindow] = useState<LogWindow | undefined>(
    undefined
  );

  const [triggerLogs, {isFetching}] = useLazyGetCloudWatchLogsQuery();

  useEffect(() => {
    if (group || groups.length === 0) return;
    const preferred = groups.find(g => g.label === 'API') ?? groups[0];
    setGroup(preferred.name);
  }, [group, groups]);

  const fetchPage = useCallback(
    async (window: LogWindow, token: string | undefined, append: boolean) => {
      setSearchError(undefined);
      const trimmed = text.trim();
      try {
        const page = await triggerLogs({
          group,
          start: window.start,
          end: window.end,
          text: trimmed || undefined,
          level,
          limit: LOG_PAGE_LIMIT,
          nextToken: token,
        }).unwrap();
        setEvents(current =>
          append ? [...current, ...page.events] : page.events
        );
        setNextToken(page.nextToken ?? undefined);
      } catch (error) {
        setSearchError(parseErrorMessage(error));
        if (!append) {
          setEvents([]);
          setNextToken(undefined);
        }
      }
    },
    [group, level, text, triggerLogs]
  );

  const search = useCallback(() => {
    if (!group) return;
    const window = logWindowFromMinutes(minutes);
    setSearchWindow(window);
    setHasSearched(true);
    void fetchPage(window, undefined, false);
  }, [fetchPage, group, minutes]);

  const loadMore = useCallback(() => {
    if (!searchWindow || !nextToken) return;
    void fetchPage(searchWindow, nextToken, true);
  }, [fetchPage, nextToken, searchWindow]);

  // CloudWatch returns events oldest-first; the console convention is newest-first.
  const newestFirst = useMemo(() => [...events].reverse(), [events]);

  return {
    group,
    setGroup,
    minutes,
    setMinutes,
    level,
    setLevel,
    text,
    setText,
    events: newestFirst,
    hasMore: nextToken !== undefined,
    hasSearched,
    isFetching,
    searchError,
    search,
    loadMore,
  };
};
